import type { Card } from './cards';
import type { GameEvent, GameState, HandState } from './types';

/** What one seat may see of a hand in progress. */
export interface HandView
  extends Omit<HandState, 'hands' | 'kitty' | 'kittyCards' | 'discards' | 'partner'> {
  /** Only the viewer's own cards; null for everyone else. */
  hands: (Card[] | null)[];
  handCounts: number[];
  kittyCount: number;
  /** The kitty as dealt, shown only to the bidder once they've picked it up. */
  kittyCards: Card[] | null;
  /** Only the viewer's own discards. */
  discards: (Card[] | null)[];
  /** A seat, null for "the bidder is alone", or 'unknown' until the card is called. Public once called. */
  partner: number | null | 'unknown';
}

export interface GameView extends Omit<GameState, 'rng' | 'hand'> {
  viewer: number | null;
  hand: HandView;
}

function partnerFor(hand: HandState): number | null | 'unknown' {
  if (hand.bidder === null) return 'unknown';
  if (hand.called !== null) return hand.partner;
  // Bidding is won but no card called yet: unknown, unless this game has no partners at all.
  return hand.kitty.length === 0 ? null : 'unknown';
}

export function viewFor(state: GameState, viewer: number | null): GameView {
  const { rng: _rng, hand, ...shared } = structuredClone(state);
  const own = (i: number) => viewer !== null && i === viewer;
  const pickedUp = hand.bidder !== null && hand.kitty.length === 0;
  const { hands, kitty, kittyCards, discards, partner: _partner, ...publicHand } = hand;
  return {
    ...shared,
    viewer,
    hand: {
      ...publicHand,
      hands: hands.map((h, i) => (own(i) ? h : null)),
      handCounts: hands.map((h) => h.length),
      kittyCount: kitty.length,
      kittyCards: pickedUp && viewer !== null && viewer === hand.bidder ? kittyCards : null,
      discards: discards.map((d, i) => (own(i) ? d : null)),
      partner: partnerFor(state.hand),
    },
  };
}

/** Events carry no hidden information (counts only), so every seat gets them as-is. */
export function eventsFor(events: readonly GameEvent[], _viewer: number | null): GameEvent[] {
  return events.slice();
}
